import { List, Typography, Tag, Avatar, Button, Popconfirm, Space, Tooltip, message, Row, Col, Empty, Card } from 'antd';
import {
  MessageOutlined,
  GlobalOutlined,
  TagOutlined,
  ClockCircleOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
  AimOutlined
} from '@ant-design/icons';
import { getStepPreview } from '../../utils/sopUtils';
import dayjs from 'dayjs';
import './StepList.css'; // 如果需要添加自定义样式
import MessagePreview from './MessagePreview'; // 引入消息预览组件
import './MessagePreview.css'; // 引入消息预览样式
import { useState } from 'react';

// 安全处理字符串，确保只对字符串调用substring方法
const safeSubstring = (value, start, end) => {
  if (typeof value !== 'string') {
    return typeof value === 'object' ? JSON.stringify(value) : String(value || '');
  }
  return value.substring(start, end);
};

// 判断步骤类型的辅助函数
const isMomentType = (step) => {
  return step.type === 'MOMENTS' ||
    step.actionType === 'MOMENTS' ||
    step.actionType === 'SEND_MOMENT';
};

const isMessageType = (step) => {
  return step.type === 'MESSAGE' ||
    ['TEXT', 'IMAGE', 'VIDEO', 'LINK', 'MINIPROGRAM', 'FILE', 'SEND_MESSAGE'].includes(step.actionType);
};

const isTagType = (step) => {
  return step.type === 'TAG' ||
    ['ADD_TAG', 'REMOVE_TAG', 'UPDATE_TAG'].includes(step.actionType);
};

// 从步骤的actionParams解析出messageList
const parseMessageListFromStep = (step) => {
  if (!step) return [];

  // 如果已经有messageList，直接返回
  if (step.messageList && Array.isArray(step.messageList) && step.messageList.length > 0) {
    return step.messageList;
  }

  // 解析actionParams
  let actionParams = step.actionParams;

  // 如果actionParams是字符串，尝试解析
  if (typeof actionParams === 'string') {
    try {
      actionParams = JSON.parse(actionParams);
    } catch (e) {
      console.error('解析步骤actionParams失败:', e);
      return [];
    }
  }

  // 如果actionParams不是对象，返回空数组
  if (!actionParams || typeof actionParams !== 'object') {
    return [];
  }

  const messageList = [];

  // 处理文本内容
  if (actionParams.text) {
    messageList.push({
      type: 'TEXT',
      params: {
        content: actionParams.text
      }
    });
  }

  // 处理附件
  if (actionParams.attachments && Array.isArray(actionParams.attachments)) {
    actionParams.attachments.forEach(attachment => {
      if (attachment.msgtype === 'image') {
        messageList.push({
          type: 'IMAGE',
          params: {
            mediaId: attachment.image.media_id || attachment.image.pic_url,
            ossUrl: attachment.image.oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'video') {
        messageList.push({
          type: 'VIDEO',
          params: {
            mediaId: attachment.video.media_id,
            ossUrl: attachment.video.oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'file') {
        messageList.push({
          type: 'FILE',
          params: {
            mediaId: attachment.file.media_id,
            ossUrl: attachment.file.oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'link') {
        messageList.push({
          type: 'LINK',
          params: {
            title: attachment.link.title || '',
            url: attachment.link.url || '',
            description: attachment.link.desc || '',
            picUrl: attachment.link.picurl || '',
            picUrl_ossUrl: attachment.link.pic_oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'miniprogram') {
        messageList.push({
          type: 'MINIPROGRAM',
          params: {
            title: attachment.miniprogram.title || '',
            appid: attachment.miniprogram.appid || '',
            pagepath: attachment.miniprogram.page || '',
            thumbMediaId: attachment.miniprogram.pic_media_id || '',
            thumbMediaId_ossUrl: attachment.miniprogram.pic_oss_url || ''
          }
        });
      }
    });
  }

  return messageList;
};

// 从步骤的actionParams解析出momentsList
const parseMomentsListFromStep = (step) => {
  if (!step) return [];

  // 如果已经有momentsList，直接返回
  if (step.momentsList && Array.isArray(step.momentsList) && step.momentsList.length > 0) {
    return step.momentsList;
  }

  // 解析actionParams
  let actionParams = step.actionParams;

  // 如果actionParams是字符串，尝试解析
  if (typeof actionParams === 'string') {
    try {
      actionParams = JSON.parse(actionParams);
    } catch (e) {
      console.error('解析步骤actionParams失败:', e);
      return [];
    }
  }

  // 如果actionParams不是对象，返回空数组
  if (!actionParams || typeof actionParams !== 'object') {
    return [];
  }

  const momentsList = [];

  // 处理文本内容
  if (actionParams.text && actionParams.text.content) {
    momentsList.push({
      type: 'MOMENT_TEXT',
      params: {
        content: actionParams.text.content
      }
    });
  }

  // 收集图片附件，稍后统一处理
  const imageAttachments = [];

  // 处理附件
  if (actionParams.attachments && Array.isArray(actionParams.attachments)) {
    // 第一步：收集所有附件，区分图片和其他类型
    actionParams.attachments.forEach(attachment => {
      if (attachment.msgtype === 'image') {
        // 收集图片附件
        imageAttachments.push({
          mediaId: attachment.image.media_id,
          ossUrl: attachment.image.oss_url || ''
        });
      } else if (attachment.msgtype === 'video') {
        momentsList.push({
          type: 'MOMENT_VIDEO',
          params: {
            mediaId: attachment.video.media_id,
            ossUrl: attachment.video.oss_url || ''
          }
        });
      } else if (attachment.msgtype === 'link') {
        momentsList.push({
          type: 'MOMENT_LINK',
          params: {
            title: attachment.link.title || '',
            url: attachment.link.url || '',
            description: attachment.link.desc || '',
            picUrl: attachment.link.picurl || '',
            picOssUrl: attachment.link.pic_oss_url || ''
          }
        });
      }
    });

    // 第二步：处理图片附件 - 创建图片组和单独图片
    if (imageAttachments.length > 0) {
      // 如果有多张图片，创建图片组
      if (imageAttachments.length > 1) {
        const groupId = 'moment-image-group-' + Date.now();
        const imageGroup = {
          type: 'MOMENT_IMAGE_GROUP',
          params: {
            imageGroup: {
              mediaIds: [],
              ossUrls: {}
            }
          }
        };

        // 添加所有图片到图片组
        imageAttachments.forEach(img => {
          imageGroup.params.imageGroup.mediaIds.push(img.mediaId);
          imageGroup.params.imageGroup.ossUrls[img.mediaId] = img.ossUrl;
        });

        // 将图片组添加到朋友圈列表
        momentsList.push(imageGroup);
      }

      // 对于所有图片，添加单独的图片项
      // 这是为了API数据保存，但不会在前端重复显示
      imageAttachments.forEach(img => {
        momentsList.push({
          type: 'MOMENT_IMAGE',
          params: {
            mediaId: img.mediaId,
            ossUrl: img.ossUrl
          }
        });
      });
    }
  }

  return momentsList;
};

const StepList = ({
  steps,
  onEditStep,
  onCopyStep,
  onDeleteStep,
  isTemplateActive = false,
  preserveOriginal = false,
  showScheduledTime = false,
  isScheduled = false,  // 新增属性：是否为定时SOP
  tagsMap = {},         // 新增属性：标签ID到标签对象的映射
  groupsMap = {},       // 新增属性：客户群ID到客户群对象的映射
  employeeMap = {}      // 新增属性：员工ID到员工对象的映射
}) => {
  // 格式化日期显示
  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    // 处理dayjs对象
    if (dayjs.isDayjs(dateStr)) {
      return dateStr.format('YYYY-MM-DD');
    }

    // 处理moment对象
    if (typeof dateStr === 'object' && dateStr._isAMomentObject) {
      return dateStr.format('YYYY-MM-DD');
    }

    // 处理可能具有format方法的其他对象
    if (typeof dateStr === 'object' && dateStr.format) {
      try {
        return dateStr.format('YYYY-MM-DD');
      } catch (e) {
        console.error('格式化日期对象失败:', e);
        return ''; // 出错时返回空字符串，避免显示Invalid date
      }
    }

    // 处理字符串或其他类型
    try {
      const parsed = dayjs(dateStr);
      if (parsed.isValid()) {
        return parsed.format('YYYY-MM-DD');
      } else {
        console.warn('无效的日期格式:', dateStr);
        return '';
      }
    } catch (e) {
      console.error('格式化日期失败:', e, dateStr);
      return ''; // 出错时返回空字符串，避免显示Invalid date
    }
  };

  // 格式化时间显示
  const formatTime = (timeStr) => {
    if (!timeStr) return '';

    // 处理dayjs对象
    if (dayjs.isDayjs(timeStr)) {
      return timeStr.format('HH:mm');
    }

    // 处理moment对象
    if (typeof timeStr === 'object' && timeStr._isAMomentObject) {
      return timeStr.format('HH:mm');
    }

    // 处理可能具有format方法的其他对象
    if (typeof timeStr === 'object' && timeStr.format) {
      try {
        return timeStr.format('HH:mm');
      } catch (e) {
        console.error('格式化时间对象失败:', e);
        return ''; // 出错时返回空字符串，避免显示Invalid date
      }
    }

    // 处理字符串
    if (typeof timeStr === 'string') {
      try {
        // 检查是否为有效时间格式
        const timeParts = timeStr.split(':');
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);

          if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            console.warn('无效的时间格式:', timeStr);
            return '';
          }

          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }

        // 尝试使用dayjs解析
        const parsed = dayjs(timeStr, 'HH:mm:ss');
        if (parsed.isValid()) {
          return parsed.format('HH:mm');
        }
      } catch (e) {
        console.error('格式化时间字符串失败:', e, timeStr);
        return ''; // 出错时返回空字符串，避免显示Invalid date
      }
    }

    // 兜底处理
    console.warn('无法识别的时间格式:', timeStr, typeof timeStr);
    return ''; // 无效时间格式，返回空
  };

  // 对步骤进行排序
  const sortStepsByTime = (stepsToSort) => {
    return [...stepsToSort].sort((a, b) => {
      // 针对不同的时间表示方式进行排序
      if (showScheduledTime) {
        // 使用绝对时间排序 (scheduledDate 和 scheduledTime)
        // 确保将日期字符串转换为dayjs对象进行比较
        let dateA, dateB;

        try {
          dateA = a.scheduledDate ? dayjs(a.scheduledDate) : dayjs('9999-12-31');
          if (!dateA.isValid()) dateA = dayjs('9999-12-31');
        } catch (e) {
          console.error('解析日期A出错:', e, a.scheduledDate);
          dateA = dayjs('9999-12-31');
        }

        try {
          dateB = b.scheduledDate ? dayjs(b.scheduledDate) : dayjs('9999-12-31');
          if (!dateB.isValid()) dateB = dayjs('9999-12-31');
        } catch (e) {
          console.error('解析日期B出错:', e, b.scheduledDate);
          dateB = dayjs('9999-12-31');
        }

        // 比较日期
        const dateDiff = dateA.diff(dateB, 'day');
        if (dateDiff !== 0) {
          return dateDiff;
        }

        // 日期相同，按时间排序
        let timeA, timeB;

        try {
          // 标准化时间格式
          const formattedTimeA = a.scheduledTime ? formatTime(a.scheduledTime) : '23:59';
          timeA = dayjs(`2000-01-01 ${formattedTimeA}`);
          if (!timeA.isValid()) timeA = dayjs('2000-01-01 23:59');
        } catch (e) {
          console.error('解析时间A出错:', e, a.scheduledTime);
          timeA = dayjs('2000-01-01 23:59');
        }

        try {
          const formattedTimeB = b.scheduledTime ? formatTime(b.scheduledTime) : '23:59';
          timeB = dayjs(`2000-01-01 ${formattedTimeB}`);
          if (!timeB.isValid()) timeB = dayjs('2000-01-01 23:59');
        } catch (e) {
          console.error('解析时间B出错:', e, b.scheduledTime);
          timeB = dayjs('2000-01-01 23:59');
        }

        return timeA.diff(timeB);
      } else {
        // 使用相对时间排序 (timeOffsetDays 和 executeTime)
        // 将数据转换为数字进行比较
        const daysA = parseInt(a.timeOffsetDays || 0, 10);
        const daysB = parseInt(b.timeOffsetDays || 0, 10);

        if (daysA !== daysB) {
          return daysA - daysB;
        }

        // 天数相同，按照执行时间排序
        // 标准化时间格式
        const timeA = a.executeTime ? a.executeTime.trim() : '23:59:59';
        const timeB = b.executeTime ? b.executeTime.trim() : '23:59:59';

        // 使用字符串比较，确保格式一致
        if (timeA === timeB) {
          // 如果时间也相同，保持原有顺序
          return 0;
        }

        return timeA.localeCompare(timeB);
      }
    });
  };

  // 渲染定时SOP的目标客户信息
  const renderTargetInfo = (step) => {
    if (!isScheduled || !step.targetRules || step.targetRules.length === 0) {
      return null;
    }

    const targetRule = step.targetRules[0]; // 通常每个步骤只有一个目标规则
    const targetType = targetRule.targetType;

    let icon, color, text, details;

    if (targetType === 'ALL') {
      icon = <AimOutlined />;
      color = 'green';
      text = '全部客户';
      details = null;
    } else if (targetType === 'TAG') {
      icon = <TagOutlined />;
      color = 'blue';
      text = '按标签';

      // 获取标签详情
      let targetIds = [];
      if (targetRule.targetIds) {
        if (typeof targetRule.targetIds === 'string') {
          try {
            targetIds = JSON.parse(targetRule.targetIds);
          } catch (e) {
            targetIds = [targetRule.targetIds];
          }
        } else if (Array.isArray(targetRule.targetIds)) {
          targetIds = targetRule.targetIds;
        }
      }

      // 根据tagsMap显示标签名称
      const tagNames = targetIds
        .map(id => {
          const tag = tagsMap[id];
          return tag ? (tag.tagName || tag.name) : id;
        })
        .filter(name => name);

      details = tagNames.length > 0 ? (
        <div style={{ marginTop: '4px', fontSize: '12px' }}>
          {tagNames.map((name, idx) => (
            <Tag key={idx} color="blue" style={{ margin: '2px' }}>
              {name}
            </Tag>
          ))}
        </div>
      ) : null;
    } else if (targetType === 'GROUP') {
      icon = <TeamOutlined />;
      color = 'purple';
      text = '按客户群';

      // 获取客户群详情
      let targetIds = [];
      if (targetRule.targetIds) {
        if (typeof targetRule.targetIds === 'string') {
          try {
            targetIds = JSON.parse(targetRule.targetIds);
          } catch (e) {
            targetIds = [targetRule.targetIds];
          }
        } else if (Array.isArray(targetRule.targetIds)) {
          targetIds = targetRule.targetIds;
        }
      }

      // 根据groupsMap显示客户群名称
      const groupNames = targetIds
        .map(id => {
          const group = groupsMap[id];
          return group ? group.name : id;
        })
        .filter(name => name);

      details = groupNames.length > 0 ? (
        <div style={{ marginTop: '4px', fontSize: '12px' }}>
          {groupNames.map((name, idx) => (
            <Tag key={idx} color="purple" style={{ margin: '2px' }}>
              {name}
            </Tag>
          ))}
        </div>
      ) : null;
    } else {
      return null; // 未知目标类型，不显示
    }

    return (
      <div style={{ marginTop: '8px' }}>
        <Tag color={color} style={{ marginRight: '8px' }}>
          {icon} 目标: {text}
        </Tag>
        {details}
      </div>
    );
  };

  // 渲染执行人信息
  const renderExecutorInfo = (step) => {
    if (!isScheduled || !step.employeeIds || step.employeeIds.length === 0 ||
      (step.targetRules && step.targetRules[0]?.targetType === 'GROUP')) {
      return null;
    }

    // 查找执行人名称
    const executorNames = step.employeeIds
      .map(id => {
        const employee = employeeMap[id];
        return employee ? employee.name : id;
      })
      .filter(name => name);

    return (
      <div style={{ marginTop: '8px' }}>
        <Tag color="cyan" style={{ marginRight: '8px' }}>
          <UserOutlined /> 执行人: {executorNames.length > 0 ? executorNames.join(', ') : '未指定'}
        </Tag>
      </div>
    );
  };

  // 对步骤进行排序
  const sortedSteps = sortStepsByTime(steps);

  // 创建原始步骤索引与排序后索引的映射关系
  const originalIndexMap = {};
  sortedSteps.forEach((step, sortedIndex) => {
    // 查找步骤在原始数组中的位置
    const originalIndex = steps.findIndex(originalStep =>
      // 如果有ID属性，使用ID比较；否则使用严格相等比较对象
      (originalStep.id && step.id && originalStep.id === step.id) ||
      originalStep === step
    );

    if (originalIndex !== -1) {
      originalIndexMap[sortedIndex] = originalIndex;
    } else {
      // 如果找不到对应的原始索引（极少情况），使用当前索引
      originalIndexMap[sortedIndex] = sortedIndex;
      console.warn('无法找到步骤的原始索引:', step);
    }
  });

  const [selectedStep, setSelectedStep] = useState(null);

  return (
    <div>
      {sortedSteps.length > 0 ? (
        <Row gutter={16}>
          <Col span={16}>
            <div className="step-list-timeline" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="timeline-container" style={{ position: 'relative' }}>
                {/* 时间线垂直线 */}
                <div 
                  className="timeline-line" 
                  style={{ 
                    position: 'absolute', 
                    left: '19px',  // 调整到圆圈中心位置
                    top: '0', 
                    bottom: '0', 
                    width: '2px', 
                    backgroundColor: '#e8e8e8',
                    zIndex: 1
                  }}
                />

                {sortedSteps.map((step, index) => {
                  // 在渲染前处理messageList和momentsList
                  const processedStep = { ...step };

                  // 如果缺少messageList，尝试从actionParams解析
                  if (isMessageType(step) && (!step.messageList || step.messageList.length === 0)) {
                    processedStep.messageList = parseMessageListFromStep(step);
                  }

                  // 调试视频步骤
                  if (step.messageList && step.messageList.some(msg => msg.type === 'VIDEO')) {
                    console.log("发现视频步骤，调试数据:", {
                      stepName: step.name,
                      messageList: step.messageList,
                      actionParams: step.actionParams
                    });

                    // 为视频消息添加备用的ossUrl恢复
                    processedStep.messageList = processedStep.messageList.map(msg => {
                      if (msg.type === 'VIDEO' && (!msg.params.ossUrl || msg.params.ossUrl === '')) {
                        // 尝试从actionParams中恢复ossUrl
                        let ossUrl = '';
                        if (step.actionParams && step.actionParams.attachments) {
                          const videoAttachment = step.actionParams.attachments.find(att =>
                            att.msgtype === 'video' && att.video && att.video.media_id === msg.params.mediaId
                          );
                          if (videoAttachment && videoAttachment.video.oss_url) {
                            ossUrl = videoAttachment.video.oss_url;
                            console.log(`为视频 ${msg.params.mediaId} 恢复 ossUrl: ${ossUrl}`);
                          }
                        }

                        return {
                          ...msg,
                          params: {
                            ...msg.params,
                            ossUrl: ossUrl || msg.params.ossUrl || ''
                          }
                        };
                      }
                      return msg;
                    });
                  }

                  // 如果缺少momentsList，尝试从actionParams解析
                  if (isMomentType(step) && (!step.momentsList || step.momentsList.length === 0)) {
                    processedStep.momentsList = parseMomentsListFromStep(step);
                  }

                  // 获取当前步骤在原始数组中的索引
                  const originalIndex = originalIndexMap[index];

                  // 确定时间节点的颜色
                  const nodeColor = isMomentType(processedStep) ? '#52c41a' :
                    isMessageType(processedStep) ? '#1890ff' :
                      isTagType(processedStep) ? '#fa8c16' : '#1890ff';

                  // 提取文本内容用于显示
                  const getTextContent = (step) => {
                    // 从 messageList 中提取文本
                    if (step.messageList && step.messageList.length > 0) {
                      const textMsg = step.messageList.find(msg => msg.type === 'TEXT');
                      if (textMsg && textMsg.params?.content) {
                        return textMsg.params.content;
                      }
                    }

                    // 从 momentsList 中提取文本
                    if (step.momentsList && step.momentsList.length > 0) {
                      const textMoment = step.momentsList.find(moment => moment.type === 'MOMENT_TEXT');
                      if (textMoment && textMoment.params?.content) {
                        return textMoment.params.content;
                      }
                    }

                    // 从 actionParams 中提取文本
                    if (step.actionParams) {
                      let params = step.actionParams;
                      if (typeof params === 'string') {
                        try {
                          params = JSON.parse(params);
                        } catch (e) {
                          // 如果是纯文本，直接返回
                          return params;
                        }
                      }

                      if (params.content) {
                        return params.content;
                      }

                      if (params.text && typeof params.text === 'object' && params.text.content) {
                        return params.text.content;
                      }

                      if (params.text && typeof params.text === 'string') {
                        return params.text;
                      }
                    }

                    return '';
                  };

                  const textContent = getTextContent(processedStep);

                  return (
                    <Card
                      key={index}
                      bordered={false}
                      headStyle={{ borderBottom: 'none', marginBottom: '0px' }}  // 移除Card标题下方的边框
                      bodyStyle={{ padding: '0px 24px 10px 24px' }}
                      style={{
                        marginBottom: '16px',
                        position: 'relative',
                        marginLeft: '60px',  // 给时间线留出更多空间
                        backgroundColor: '#fafafa',  // 浅灰色背景
                        // minHeight: '50px'
                      }}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {/* 添加类型图标 */}
                            <div style={{ marginRight: '8px', color: isMomentType(processedStep) ? '#52c41a' : '#1890ff' }}>
                              {isMomentType(processedStep) ? <GlobalOutlined /> : <MessageOutlined />}
                            </div>
                            <div style={{ marginRight: '16px', fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
                              {showScheduledTime ? (
                                <>
                                  <Typography.Title level={5} style={{ marginBottom: '0px' }}>
                                    {processedStep.scheduledDate ? dayjs(processedStep.scheduledDate).format('MM-DD') : ''} &nbsp;
                                    {formatTime(processedStep.scheduledTime)}
                                  </Typography.Title>
                                </>
                              ) : (
                                <>
                                  <Typography.Title level={5} style={{ marginBottom: '0px' }}>
                                    第{processedStep.timeOffsetDays || 0}天 &nbsp; {processedStep.executeTime ? (typeof processedStep.executeTime === 'string'
                                        ? processedStep.executeTime.substring(0, 5)
                                        : '')
                                      : '全天'}
                                  </Typography.Title>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      }
                      extra={
                        <Space>
                          <Tooltip title={isTemplateActive ? "激活状态的模板不允许编辑步骤" : ""}>
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => onEditStep(step, originalIndex)}
                              disabled={isTemplateActive}
                            />
                          </Tooltip>
                          <Tooltip title={isTemplateActive ? "激活状态的模板不允许复制步骤" : ""}>
                            <Button
                              icon={<CopyOutlined />}
                              onClick={() => onCopyStep(step)}
                              disabled={isTemplateActive}
                            />
                          </Tooltip>
                          <Tooltip title={isTemplateActive ? "激活状态的模板不允许删除步骤" : ""}>
                            <Popconfirm
                              title="确定要删除此步骤吗？"
                              onConfirm={() => onDeleteStep(originalIndex)}
                              okText="确定"
                              cancelText="取消"
                              disabled={isTemplateActive}
                            >
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={isTemplateActive}
                              />
                            </Popconfirm>
                          </Tooltip>
                        </Space>
                      }
                      onClick={() => {
                        if (isMessageType(processedStep) || isMomentType(processedStep)) {
                          setSelectedStep(processedStep);
                        }
                      }}
                    >
                      <Typography.Title level={5}>{processedStep.name}</Typography.Title>

                      {/* 文本内容显示 */}
                      {textContent && (
                        <Typography.Paragraph
                          style={{ margin: '0 0 8px 0', color: '#666' }}
                          ellipsis={{ rows: 2, expandable: false }}
                        >
                          {textContent}
                        </Typography.Paragraph>
                      )}

                      {/* 时间节点 */}
                      <div 
                        className="timeline-node" 
                        style={{ 
                          position: 'absolute', 
                          left: '-40px',  // 调整到时间线上
                          top: '20px',  
                          width: '12px', 
                          height: '12px', 
                          borderRadius: '50%', 
                          border: `2px solid ${nodeColor}`,  
                          backgroundColor: '#fafafa', // 使用与card相同的背景色，确保线不穿过
                          zIndex: 2
                        }}
                      />

                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                          {/* 媒体内容数量标签 */}
                          {(() => {
                            let attachmentCount = 0;

                            if (processedStep.messageList && processedStep.messageList.length > 0) {
                              // 先处理图片组，避免重复计算
                              const processedMediaIds = new Set();

                              // 第一遍：处理图片组
                              processedStep.messageList.forEach(msg => {
                                if (msg.type === 'IMAGE_GROUP' && msg.params?.imageGroup?.mediaIds) {
                                  // 图片集中每张图片都算一个附件
                                  attachmentCount += msg.params.imageGroup.mediaIds.length;
                                  // 记录已处理的图片ID
                                  msg.params.imageGroup.mediaIds.forEach(id => {
                                    processedMediaIds.add(id);
                                  });
                                }
                              });

                              // 第二遍：处理其他附件（跳过已在图片组中的图片）
                              processedStep.messageList.forEach(msg => {
                                // 文字类型不算附件
                                if (msg.type === 'TEXT') return;

                                // 跳过图片组，已在第一遍处理
                                if (msg.type === 'IMAGE_GROUP') return;

                                // 跳过已在图片组中的单独图片
                                if (msg.type === 'IMAGE' && msg.params?.mediaId && processedMediaIds.has(msg.params.mediaId)) {
                                  return;
                                }

                                // 其他类型每个都算1个附件
                                attachmentCount += 1;
                              });
                            }

                            if (processedStep.momentsList && processedStep.momentsList.length > 0) {
                              // 先处理图片组，避免重复计算
                              const processedMediaIds = new Set();

                              // 第一遍：处理图片组
                              processedStep.momentsList.forEach(moment => {
                                if (moment.type === 'MOMENT_IMAGE_GROUP' && moment.params?.imageGroup?.mediaIds) {
                                  // 图片集中每张图片都算一个附件
                                  attachmentCount += moment.params.imageGroup.mediaIds.length;
                                  // 记录已处理的图片ID
                                  moment.params.imageGroup.mediaIds.forEach(id => {
                                    processedMediaIds.add(id);
                                  });
                                }
                              });

                              // 第二遍：处理其他附件（跳过已在图片组中的图片）
                              processedStep.momentsList.forEach(moment => {
                                // 文字类型不算附件
                                if (moment.type === 'MOMENT_TEXT') return;

                                // 跳过图片组，已在第一遍处理
                                if (moment.type === 'MOMENT_IMAGE_GROUP') return;

                                // 跳过已在图片组中的单独图片
                                if (moment.type === 'MOMENT_IMAGE' && moment.params?.mediaId && processedMediaIds.has(moment.params.mediaId)) {
                                  return;
                                }

                                // 其他类型每个都算1个附件
                                attachmentCount += 1;
                              });
                            }

                            return attachmentCount > 0 && (
                              <Tag color={isMessageType(processedStep) ? "cyan" : "green"}>
                                {attachmentCount} 个附件
                              </Tag>
                            );
                          })()}

                          {/* 渲染定时SOP的目标标签 - 显示具体信息 */}
                          {isScheduled && processedStep.targetRules && processedStep.targetRules.length > 0 && (() => {
                            const targetRule = processedStep.targetRules[0];
                            const targetType = targetRule.targetType;
                            
                            if (targetType === 'ALL') {
                              return <Tag color="green"><AimOutlined /> 全部客户</Tag>;
                            } else if (targetType === 'TAG') {
                              // 获取标签详情
                              let targetIds = [];
                              if (targetRule.targetIds) {
                                if (typeof targetRule.targetIds === 'string') {
                                  try {
                                    targetIds = JSON.parse(targetRule.targetIds);
                                  } catch (e) {
                                    targetIds = [targetRule.targetIds];
                                  }
                                } else if (Array.isArray(targetRule.targetIds)) {
                                  targetIds = targetRule.targetIds;
                                }
                              }
                              
                              // 根据tagsMap显示标签名称
                              const tagNames = targetIds
                                .map(id => {
                                  const tag = tagsMap[id];
                                  return tag ? (tag.tagName || tag.name) : id;
                                })
                                .filter(name => name);
                              
                              return tagNames.map((name, idx) => (
                                <Tag key={idx} color="blue">
                                  <TagOutlined /> {name}
                                </Tag>
                              ));
                            } else if (targetType === 'GROUP') {
                              // 获取客户群详情
                              let targetIds = [];
                              if (targetRule.targetIds) {
                                if (typeof targetRule.targetIds === 'string') {
                                  try {
                                    targetIds = JSON.parse(targetRule.targetIds);
                                  } catch (e) {
                                    targetIds = [targetRule.targetIds];
                                  }
                                } else if (Array.isArray(targetRule.targetIds)) {
                                  targetIds = targetRule.targetIds;
                                }
                              }
                              
                              // 根据groupsMap显示客户群名称
                              const groupNames = targetIds
                                .map(id => {
                                  const group = groupsMap[id];
                                  return group ? group.name : id;
                                })
                                .filter(name => name);
                              
                              return groupNames.map((name, idx) => (
                                <Tag key={idx} color="purple">
                                  <TeamOutlined /> {name}
                                </Tag>
                              ));
                            }
                            return null;
                          })()}

                          {/* 渲染执行人标签 - 显示具体姓名 */}
                          {isScheduled && processedStep.employeeIds && processedStep.employeeIds.length > 0 &&
                           !(processedStep.targetRules && processedStep.targetRules[0]?.targetType === 'GROUP') && (() => {
                             // 查找执行人名称
                             const executorNames = processedStep.employeeIds
                               .map(id => {
                                 const employee = employeeMap[id];
                                 return employee ? employee.name : id;
                               })
                               .filter(name => name);

                             return executorNames.map((name, idx) => (
                               <Tag key={idx} color="cyan">
                                 <UserOutlined /> {name}
                               </Tag>
                             ));
                           })()}
                        </div>
                      </Space>
                    </Card>
                  );
                })}
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div className="shared-preview" style={{ padding: '16px', borderRadius: '4px', minHeight: '400px', marginLeft: '20px' }}>
              {selectedStep ? (
                <>
                  <MessagePreview step={selectedStep} noTruncate={true} />
                </>
              ) : (
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="请选择一个消息步骤查看预览"
                />
              )}
            </div>
          </Col>
        </Row>
      ) : (
        <div className="empty-list">暂无内容，请添加内容。</div>
      )}
    </div>
  );
};

export default StepList;